import React, { useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';
import { runInAction } from 'mobx';

import { UserEntity } from 'Models/Entities';
import { useOnMount } from 'Hooks/useMounted';
import useDebounce from 'Hooks/useDebounce';
import alertToast from 'Util/ToastifyUtils';

import SecuredPage from 'Views/Components/Security/SecuredPage';
import Navbar from 'Views/Components/Navbar/Navbar';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { DisplayType } from 'Views/Components/Models/Enums';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { tabs } from 'Views/Pages/Frontend/UserManagement/UserManagementPage';

import { getSingleStudent } from 'Queries/GraphQlQueries/User/UserEntityQueries';
import { inviteUser } from 'Queries/REST/Register/RegisterRequests';

interface IStudentFormProps {
	existingUserId?: string;
	onCancel: () => any;
	afterSave: () => any;
}

export const StudentFormPage = () => {
	const { userId } = useParams<{ userId?: string }>();
	const history = useHistory();

	return (
		<SecuredPage
			groups={[
				'Super Administrators',
				'Admin',
				'InstitutionAdmin',
				'StudentAdvisor',
			]}
		>
			<div className="body-content">
				<Navbar />
				<StudentForm
					existingUserId={userId}
					afterSave={() => history.push(`/users?tab=${tabs[1]}`)}
					onCancel={history.goBack}
				/>
			</div>
		</SecuredPage>
	);
};

export const StudentForm: React.FunctionComponent<IStudentFormProps> = observer(({
	existingUserId,
	afterSave,
	onCancel,
}) => {
	const formStore = useLocalStore(() => ({ user: new UserEntity() }));

	const [loadingExistingUser, setLoadingExistingUser] = useState(!!existingUserId);
	const [isSubmitting, setSubmitting] = useState(false);

	useOnMount(
		async (isMounted, abortSignals) => {
			if (existingUserId) {
				const userData = await getSingleStudent(abortSignals, existingUserId);

				if (isMounted()) {
					setLoadingExistingUser(false);
					runInAction(() => {
						formStore.user = new UserEntity(userData.data.userEntity);
					});
				}
			}
		},
		[existingUserId],
	);

	const onSave = useDebounce(
		async abortSignals => {
			const editingExistingUser = !!formStore.user.id;

			// save edit user
			if (editingExistingUser) {
				formStore.user.save()
					.then(() => {
						alertToast(
							`User "${formStore.user.firstName} ${formStore.user.lastName}" saved`,
							'success',
						);
						afterSave();
					})
					.catch(requestError => {
						console.error(requestError);
						alertToast(
							'User failed to be edited',
							'error',
						);
					})
					.finally(() => {
						setSubmitting(false);
					});
			} else {
				if (isSubmitting || loadingExistingUser) {
					return;
				}
				setSubmitting(true);
				inviteUser(abortSignals, formStore.user)
					.then(() => {
						alertToast(
							`User "${formStore.user.firstName} ${formStore.user.lastName}" created`,
							'success',
						);
						afterSave();
					})
					.catch(requestError => {
						console.error(requestError);
						alertToast(
							'User failed to be created',
							'error',
						);
					})
					.finally(() => {
						setSubmitting(false);
					});
			}
		},
		[formStore.user.id],
	);

	const validate = async () => {
		if (!formStore.user) {
			return false;
		}
		formStore.user.clearErrors();
		await formStore.user.validate({
			studentId: {}, firstName: {}, lastName: {}, email: {},
		});

		return formStore.user.hasValidationError;
	};

	const onSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
		evt.preventDefault();
		evt.stopPropagation();

		const hasValidationErrors = await validate();

		if (!hasValidationErrors) {
			onSave();
		}
	};

	return (
		<main className="user-form-page student">
			<form onSubmit={onSubmit}>
				<div className="form-fields">
					<h3 className="title">{existingUserId ? 'Edit' : 'Add'} student</h3>
					<TextField
						model={formStore.user}
						id="studentId"
						modelProperty="studentId"
						name="Student ID"
						displayType={DisplayType.BLOCK}
						label="Student ID"
						placeholder="Enter student ID"
						isRequired
						errors={formStore.user.getErrorsForAttribute('studentId')}
					/>
					<TextField
						model={formStore.user}
						id="firstName"
						modelProperty="firstName"
						name="First name"
						placeholder="Enter first name"
						displayType={DisplayType.BLOCK}
						label="First name"
						isRequired
						errors={formStore.user.getErrorsForAttribute('firstName')}
					/>
					<TextField
						model={formStore.user}
						id="lastName"
						modelProperty="lastName"
						name="Last name"
						placeholder="Enter last name"
						displayType={DisplayType.BLOCK}
						label="Last name"
						isRequired
						errors={formStore.user.getErrorsForAttribute('lastName')}
					/>
					<TextField
						model={formStore.user}
						id="email"
						modelProperty="email"
						name="Email"
						placeholder="Enter email"
						displayType={DisplayType.BLOCK}
						label="Email"
						isRequired
						errors={formStore.user.getErrorsForAttribute('email')}
					/>
				</div>
				<div className="actions">
					<Button onClick={onCancel}>
						Cancel
					</Button>
					<Button
						type="submit"
						colors={Colors.Primary}
						display={Display.Solid}
						disabled={isSubmitting}
					>
						Save
					</Button>
				</div>
			</form>
		</main>
	);
});
